import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  Dialog,
  ErrorText,
  HelpTip,
  cn,
  useToast,
  type DigestRecipient,
  type DigestSettings,
} from '@dataroom/shared';
import {
  useDigestPreview,
  useDigestSettings,
  useSaveDigestSettings,
  useSendDigestPreview,
} from '@/api/hooks';
import {
  DIGEST_AUDIENCE_LABELS,
  DIGEST_DAYS,
  digestSettingsBody,
  formatInZone,
  hourLabel,
  sameDigestSettings,
} from '@/lib/digest';
import { apiMessage } from '@/lib/errors';

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

const HOURS = Array.from({ length: 24 }, (_, h) => h);

const AUDIENCE_SWITCHES: {
  key: 'sendToAdmin' | 'sendToDealTeam' | 'sendToSeller' | 'sendToBuyers';
  label: string;
  hint: string;
}[] = [
  { key: 'sendToAdmin', label: 'Room admin (you)', hint: 'The full deal-side edition.' },
  { key: 'sendToDealTeam', label: 'Deal team', hint: 'The full deal-side edition.' },
  {
    key: 'sendToSeller',
    label: 'Seller',
    hint: 'Released checklist phases and what is shared with the seller.',
  },
  {
    key: 'sendToBuyers',
    label: 'Buyers',
    hint: 'Each buyer sees only their own progress and what is shared with everyone.',
  },
];

/**
 * The room's WEEKLY DIGEST (ADR-0047) on the Settings subpage: on/off, the day
 * and hour (in the room's timezone), which roles get it, and who will actually
 * receive it (someone who unsubscribed stays unsubscribed — these settings never
 * override that). "Preview" shows the email as it would go out right now, as the
 * admin or as the seller (the plain-text part here, the graphical email in its own tab); "Send a preview to me now"
 * emails the admin their own copy.
 */
export function WeeklyDigestCard({ roomId, readOnly }: { roomId: string; readOnly: boolean }) {
  const view = useDigestSettings(roomId);
  const save = useSaveDigestSettings(roomId);
  const sendPreview = useSendDigestPreview(roomId);
  const toast = useToast();
  const data = view.data;
  const saved = data?.settings ?? null;
  const [draft, setDraft] = useState<DigestSettings | null>(saved);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Seed the form from the saved settings once they load (and after a save).
  useEffect(() => {
    if (saved) setDraft(saved);
  }, [saved]);

  const dirty = saved !== null && draft !== null && !sameDigestSettings(saved, draft);
  const set = <K extends keyof DigestSettings>(key: K, value: DigestSettings[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Weekly digest <HelpTip id="admin.settings.digest" className="ml-1" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          A one-page email each week with checklist progress, what changed, the planned timeline and
          open risks. Each person gets only what they can already see in the room.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {view.error ? (
          <ErrorText error={view.error} />
        ) : !data || !draft ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <label className="flex w-fit items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={draft.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
                disabled={readOnly || save.isPending}
              />
              Send the weekly digest
            </label>

            <fieldset
              className="grid gap-3 sm:grid-cols-2"
              disabled={readOnly || save.isPending || !draft.enabled}
            >
              <legend className="sr-only">When it goes out</legend>
              <div className="space-y-1.5">
                <label htmlFor="digest-day" className="text-sm font-medium">
                  Day
                </label>
                <select
                  id="digest-day"
                  className={SELECT_CLASS}
                  value={draft.dayOfWeek}
                  onChange={(e) => set('dayOfWeek', Number(e.target.value))}
                >
                  {DIGEST_DAYS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="digest-hour" className="text-sm font-medium">
                  Time
                </label>
                <select
                  id="digest-hour"
                  className={SELECT_CLASS}
                  value={draft.hourOfDay}
                  onChange={(e) => set('hourOfDay', Number(e.target.value))}
                  aria-describedby="digest-zone"
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
              </div>
            </fieldset>
            <p id="digest-zone" className="text-xs text-muted-foreground">
              Room time: {data.timeZone}.{' '}
              {data.nextSendAt
                ? `Next digest: ${formatInZone(data.nextSendAt, data.timeZone)}.`
                : 'The digest is off — nothing will be sent.'}
              {dirty ? ' Save to update.' : ''}
            </p>

            <fieldset className="space-y-2" disabled={readOnly || save.isPending}>
              <legend className="text-sm font-medium">Who gets it</legend>
              {AUDIENCE_SWITCHES.map((s) => (
                <label key={s.key} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={draft[s.key]}
                    onChange={(e) => set(s.key, e.target.checked)}
                  />
                  <span>
                    {s.label}
                    <span className="block text-xs text-muted-foreground">{s.hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            {readOnly ? null : (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  size="sm"
                  disabled={!dirty || save.isPending}
                  onClick={() =>
                    save.mutate(digestSettingsBody(draft), {
                      onSuccess: () => toast({ message: 'Saved the weekly digest settings' }),
                    })
                  }
                >
                  {save.isPending ? 'Saving…' : 'Save digest settings'}
                </Button>
                {save.error ? (
                  <p className="text-sm text-destructive">{apiMessage(save.error)}</p>
                ) : null}
              </div>
            )}

            <RecipientList recipients={data.recipients} />

            <div className="flex flex-wrap items-center gap-3 border-t pt-4">
              <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)}>
                Preview
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={sendPreview.isPending}
                onClick={() =>
                  sendPreview.mutate(undefined, {
                    onSuccess: () =>
                      toast({ message: 'Sent a preview of the digest to your email' }),
                  })
                }
              >
                {sendPreview.isPending ? 'Sending…' : 'Send a preview to me now'}
              </Button>
              {sendPreview.error ? (
                <p className="text-sm text-destructive">{apiMessage(sendPreview.error)}</p>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
      {previewOpen ? (
        <DigestPreviewDialog
          roomId={roomId}
          hasSeller={(data?.recipients ?? []).some((r) => r.audience === 'seller')}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </Card>
  );
}

function RecipientList({ recipients }: { recipients: DigestRecipient[] }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Recipients</h3>
      {recipients.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nobody is in this room yet.</p>
      ) : (
        <ul className="divide-y rounded-md border" aria-label="Digest recipients">
          {recipients.map((r) => (
            <li key={r.actorId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm">{r.name}</p>
                <p className="break-all text-xs text-muted-foreground">
                  {DIGEST_AUDIENCE_LABELS[r.audience]}
                  {r.email && r.email !== r.name ? ` · ${r.email}` : ''}
                </p>
              </div>
              {r.optedOut ? (
                <Chip tone="na" icon="minus">
                  Unsubscribed
                </Chip>
              ) : r.unconfirmed ? (
                <Chip tone="neutral">
                  Unconfirmed — won&apos;t receive until they confirm their email
                </Chip>
              ) : r.willReceive ? (
                <Chip tone="certified" icon="check">
                  Will receive
                </Chip>
              ) : (
                <Chip tone="neutral">Not included</Chip>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Based on the saved settings. Anyone can unsubscribe with the link in the email; their access
        to the room does not change.
      </p>
    </div>
  );
}

function DigestPreviewDialog({
  roomId,
  hasSeller,
  onClose,
}: {
  roomId: string;
  hasSeller: boolean;
  onClose: () => void;
}) {
  const [as, setAs] = useState<'admin' | 'seller'>('admin');
  const preview = useDigestPreview(roomId, as, true);
  const toggle = (value: 'admin' | 'seller', label: string, disabled = false) => (
    <button
      type="button"
      aria-pressed={as === value}
      disabled={disabled}
      onClick={() => setAs(value)}
      className={cn(
        'h-8 rounded-md px-3 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
        as === value ? 'bg-primary text-primary-foreground' : 'text-ink-2 hover:bg-surface-2',
      )}
    >
      {label}
    </button>
  );

  return (
    <Dialog
      title="Weekly digest preview"
      description={
        preview.data
          ? `Subject: ${preview.data.subject}`
          : 'The email as it would go out right now.'
      }
      size="lg"
      onClose={onClose}
      headerAside={
        <div role="group" aria-label="Preview as" className="flex rounded-md border p-0.5">
          {toggle('admin', 'As me')}
          {toggle('seller', 'As the seller', !hasSeller)}
        </div>
      }
      footer={
        <>
          <span className="text-xs text-muted-foreground">
            {hasSeller ? 'Nothing is sent from here.' : 'No seller is bound to this room yet.'}
          </span>
          <Button size="sm" variant="outline" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {preview.isPending ? (
        <p className="text-sm text-muted-foreground">Loading the preview…</p>
      ) : preview.error ? (
        <ErrorText error={preview.error} />
      ) : (
        // The app's CSP (style-src 'self') rightly refuses an email's inline
        // styles inside the app, so the graphical email opens as its own page
        // (served by the API under its own no-script, inline-style-only policy);
        // here the plain-text part shows what it says.
        <div className="space-y-3">
          <a
            href={`/api/v1/rooms/${encodeURIComponent(roomId)}/digest/preview.html?as=${as}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Open the email {as === 'seller' ? 'as the seller gets it' : 'as you get it'} ↗
          </a>
          <pre
            aria-label={
              as === 'seller' ? 'Digest text as the seller gets it' : 'Digest text as you get it'
            }
            className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-md border bg-surface-2 p-3 font-mono text-xs leading-5"
          >
            {preview.data.text}
          </pre>
        </div>
      )}
    </Dialog>
  );
}
